/* eslint-disable react/no-unused-prop-types, react/static-property-placement */
import React from 'react';
import PropTypes from 'prop-types';
import TrackingCodes from './TrackingCodes';
import PersistentAdobeTaggingKeys from './TaggingKeys';
import DummySatellite from './DummySatellite';

/**
 * ADOBE ANALYTICS DTM class
 */

export const AnalyticsContext = React.createContext([]);

const propTypes = {
    adobeTaggingObj: PropTypes.object,
    analyticsContainerObj: PropTypes.object,
    eventType: PropTypes.object, // TODO: Review if this is needed
    trackingObject: PropTypes.object,
    trackingVars: PropTypes.object,
};

const defaultProps = {
    adobeTaggingObj: {},
    analyticsContainerObj: window,
    eventType: TrackingCodes.eventTypes, // TODO: Review if this is needed
    trackingVars: TrackingCodes.trackingVars,
    trackingObject: {
        componentId: TrackingCodes.trackingValues.componentIds.virtual_car,
        toolName: TrackingCodes.trackingValues.toolName,
    },
};

class Analytics extends React.Component {
    /**
     * Create a DTM Analytics instance
     * @param {Object} adobeTaggingObj
     */
    constructor(props) {
        super(props);

        this.adobeTagging = props.adobeTaggingObj;
        this.analyticsContainerObj = props.analyticsContainerObj;
        this.persistentTaggingProperties = PersistentAdobeTaggingKeys;
        this.dummySatellite = new DummySatellite();
        this.pageloadTracking();

        if (!this.adobeTagging) {
            throw new Error('There`s no Adobe Tagging object');
        }
    }

    pageloadTracking = () => {
        const pageLoadTrackingObject = { ...this.adobeTagging };
        window.googleDataLayer = [pageLoadTrackingObject, { event: 'DLready' }];
    };

    getTrackingVars = (eventName) => {
        const { trackingVars } = this.props;

        return trackingVars[eventName];
    };

    getDefaultOfTrackVar = (trackingVar) => {
        const { trackingObject } = this.props;

        return trackingObject[trackingVar];
    };

    processEventObject = (eventObject, eventName) => {
        const computedEventObject = eventObject || {};
        const computedTrackingVarsObject = this.getTrackingVars(eventName);

        Object.keys(computedTrackingVarsObject).forEach((key) => {
            if (!computedEventObject[key]) {
                const defaultValues = this.getDefaultOfTrackVar(key);

                if (defaultValues) {
                    computedEventObject[key] = defaultValues;
                } else {
                    console.info(`${key}'s value is missing in the tracking object which is mandatory`);
                }
            }
        });

        Object.keys(computedEventObject).forEach((key) => {
            if (typeof computedEventObject[key] === 'string') {
                computedEventObject[key] = computedEventObject[key].trim();

                if (key === 'destination') {
                    const absoluteUrl = new URL(computedEventObject[key], `${window.location.protocol}//${window.location.hostname}`);
                    computedEventObject[key] = absoluteUrl.href;
                }
            }
        });

        return computedEventObject;
    };

    trackEvent = (eventName, eventObject) => {
        const satellite = this.analyticsContainerObj._satellite || this.dummySatellite;
        const processedEventObject = this.processEventObject(eventObject, eventName);

        this.resetHeliosTaggingObject();

        if (processedEventObject) {
            this.updateTaggingObject(processedEventObject);
        }

        satellite.track(eventName);

        if (this.analyticsContainerObj.googleDataLayer) {
            this.analyticsContainerObj.googleDataLayer.push({ ...this.adobeTagging }, { event: eventName });
        }
    };

    updateTaggingObject = (dataObj) => {
        this.adobeTagging = Object.assign(this.adobeTagging, dataObj || {});
    };

    resetHeliosTaggingObject = () => {
        Object.keys(this.adobeTagging).forEach((key) => {
            if (!this.persistentTaggingProperties.includes(key)) {
                delete this.adobeTagging[key];
            }
        });
    };

    render() {
        const { children } = this.props;
        return (
            <AnalyticsContext.Provider
                value={{
                    trackEvent: this.trackEvent,
                    updateTaggingObject: this.updateTaggingObject,
                }}
            >
                {children}
            </AnalyticsContext.Provider>
        );
    }
}

Analytics.propTypes = propTypes;
Analytics.defaultProps = defaultProps;

export default Analytics;
